import React, { useState, useEffect, useRef } from 'react';
import MarketCard from './MarketCard';

export default function MarketCarousel({ markets }) {
  const [current, setCurrent] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const len = markets?.length || 0;
  const carouselRef = useRef(null);
  
  // Reference to track if interaction started on a button
  const interactionStartedOnButton = useRef(false);

  // Touch handling state
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const minSwipeDistance = 50; // Minimum swipe distance in pixels

  const prev = () => {
    if (transitioning) return;
    setTransitioning(true);
    setCurrent(c => (c - 1 + len) % len);
    setTimeout(() => setTransitioning(false), 500);
  };

  const next = () => {
    if (transitioning) return;
    setTransitioning(true);
    setCurrent(c => (c + 1) % len);
    setTimeout(() => setTransitioning(false), 500);
  };

  // Handle swipe from MarketCard
  const handleCardSwipe = (direction) => {
    if (direction === 'left') {
      next();
    } else if (direction === 'right') {
      prev();
    }
  };

  // Touch event handlers
  const handleTouchStart = (e) => {
    // Check if the touch started on a button
    if (e.target.tagName.toLowerCase() === 'button') {
      interactionStartedOnButton.current = true;
      return;
    }
    
    interactionStartedOnButton.current = false;
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    if (interactionStartedOnButton.current) return;
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (interactionStartedOnButton.current) {
      interactionStartedOnButton.current = false;
      return;
    }
    
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) next();
    if (isRightSwipe) prev();

    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  // Mouse drag handling
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragEndX = useRef(0);

  const handleMouseDown = (e) => {
    // Check if the mouse down event happened on a button
    if (e.target.tagName.toLowerCase() === 'button') {
      interactionStartedOnButton.current = true;
      return;
    }
    
    interactionStartedOnButton.current = false;
    isDragging.current = true;
    dragStartX.current = e.clientX;
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current || interactionStartedOnButton.current) return;
    dragEndX.current = e.clientX;
  };

  const handleMouseUp = (e) => {
    if (interactionStartedOnButton.current) {
      interactionStartedOnButton.current = false;
      return;
    }
    
    if (!isDragging.current) return;

    const distance = dragStartX.current - dragEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) next();
    if (isRightSwipe) prev();

    isDragging.current = false;
    dragStartX.current = 0;
    dragEndX.current = 0;
  };

  const handleMouseLeave = () => {
    if (isDragging.current) {
      isDragging.current = false;
    }
  };

  if (!markets || len === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 border-2 border-violet-500 rounded-full relative flex items-center justify-center">
            <div className="w-10 h-10 bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 rounded-full animate-pulse"></div>
            <div className="absolute inset-0 border-t-2 border-violet-500 rounded-full animate-spin"></div>
          </div>
          <div className="mt-4 font-mono text-sm">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
              Loading Markets...
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={carouselRef}
      className="relative pb-8 w-full h-96 flex items-center justify-center overflow-hidden cursor-grab active:cursor-grabbing"
      style={{ perspective: '1200px' }}
      role="region"
      aria-label="Markets carousel"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      {/* Carousel swipe indicators */}
      <div className="absolute top-0 left-0 h-full w-12 bg-gradient-to-r from-black/10 to-transparent z-10 pointer-events-none"></div>
      <div className="absolute top-0 right-0 h-full w-12 bg-gradient-to-l from-black/10 to-transparent z-10 pointer-events-none"></div>

      {/* Carousel cards */}
      <div className="relative w-72 h-80">
        {markets.map((market, i) => {
          const offset = i - current;
          const half = Math.floor(len / 2);
          let realOffset = offset;
          if (offset > half) realOffset = offset - len;
          if (offset < -half) realOffset = offset + len;

          const abs = Math.abs(realOffset);
          const zIndex = len - abs;
          const translateX = realOffset * 80;
          const rotateY = realOffset * -25;
          const scale = 1 - abs * 0.15;
          const opacity = 1 - abs * 0.3;

          return (
            <div
              key={market.id}
              className="absolute top-0 left-1/2 transform-gpu"
              style={{
                transform: `
                  translateX(${translateX}px)
                  translateX(-50%)
                  rotateY(${rotateY}deg)
                  scale(${scale})
                `,
                zIndex,
                opacity,
                transition: 'all 0.5s cubic-bezier(0.19, 1, 0.22, 1)'
              }}
            >
              <MarketCard
                market={market}
                onBet={(direction) => {
                  // handleBet(direction, market);
                }}
                onSwipe={handleCardSwipe}
                isActive={i === current}
              />
            </div>
          );
        })}
      </div>

      {/* Navigation dots */}
      <div className="absolute bottom-0 left-0 right-0 z-20 flex justify-center space-x-2 pb-2">
        {markets.map((_, i) => (
          <button
            key={i}
            onClick={() => {
              if (transitioning) return;
              setTransitioning(true);
              setCurrent(i);
              setTimeout(() => setTransitioning(false), 500);
            }}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              i === current
                ? 'bg-gradient-to-r from-violet-500 to-pink-500 w-4'
                : 'bg-gray-600 hover:bg-gray-500'
            }`}
            aria-label={`Go to market ${i + 1}`}
            aria-current={i === current ? 'true' : 'false'}
          />
        ))}
      </div>
    </div>
  );
}
